// 平台使用时长工具：负责读写 localStorage、识别用户、把秒数格式化成「X小时Y分」。
// 说明：后端暂无统计接口，所以时长存在浏览器本地；换电脑/清缓存会丢失。
import type { UserInfo } from "@/api/user";

// localStorage 的 key 前缀，完整示例：platformUsageSeconds:12345。
export const PLATFORM_USAGE_STORAGE_PREFIX = "platformUsageSeconds:";

/**
 * 用当前登录用户的 userId 作为 localStorage 的区分 key。
 *
 * 为什么需要这个？
 * - 同一台电脑可能登录过多个账号，每个账号的时长要分开存。
 * - localStorage 是按 key 存值的，key 里必须带上「是谁的数据」。
 *
 * @param user 当前登录用户（来自 userStore）
 * @returns userId 字符串；未登录或 userId 为空时返回 ""
 */
export function resolvePlatformUsageUserKey(
  user: Pick<UserInfo, "userId"> | null | undefined
): string {
  return String(user?.userId ?? "").trim();
}

/**
 * 读取某个用户已累计的平台使用秒数（从 localStorage）。
 *
 * 个人中心页面调用此函数展示时长；Tracker 在绑定用户时也会调用，加载历史累计值。
 *
 * @param userKey resolvePlatformUsageUserKey 的返回值
 * @returns 累计秒数；无 key、无数据或数据非法时返回 0
 */
export function readPlatformUsageSeconds(userKey: string): number {
  if (!userKey) return 0;

  const storageKey = `${PLATFORM_USAGE_STORAGE_PREFIX}${userKey}`;
  const raw = localStorage.getItem(storageKey);
  const seconds = Number(raw ?? 0);

  // 只接受正整数，避免 localStorage 被手动改坏导致 NaN
  return Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
}

/**
 * 把累计秒数写入 localStorage（持久化）。
 *
 * Tracker 每 10 秒、切后台、关页面前都会调用，否则数据只留在内存里，刷新/个人中心读不到。
 *
 * @param userKey resolvePlatformUsageUserKey 的返回值
 * @param seconds 要保存的累计秒数（会自动取整、且不小于 0）
 */
export function writePlatformUsageSeconds(
  userKey: string,
  seconds: number
): void {
  if (!userKey) return;

  localStorage.setItem(
    `${PLATFORM_USAGE_STORAGE_PREFIX}${userKey}`,
    String(Math.max(0, Math.floor(seconds)))
  );
}

/**
 * 把「总秒数」转成个人中心上的中文文案。
 *
 * 规则（与设计稿「2小时12分」一致）：
 * - ≥ 1 小时：X小时Y分（Y 可以是 0，例如 1小时0分）
 * - ≥ 1 分钟且 < 1 小时：X分
 * - < 1 分钟：不足1分
 *
 * @param totalSeconds 累计秒数
 * @returns 展示用字符串，例如 "1小时2分"、"35分"、"不足1分"
 */
export function formatPlatformUsageDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) return `${hours}小时${minutes}分`;
  if (minutes > 0) return `${minutes}分`;
  return "不足1分";
}
